import React, { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import ScanAuditOverviewCard from "@/modules/racket_lab_admin/ScanAuditOverviewCard";
import useAdminApi from "@/hooks/useAdminApi";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminOpsScanAuditOverview — Sub-sprint D-β-α.
 *
 * Read-only filterable overview of `qr_tokens.scan_audit[]` aggregates
 * and `qr_resolve_pre_lookup_denials`. Filters: organisation_id
 * (locked to pilot in v0), kind, result, limit.
 */
const RLAdminOpsScanAuditOverview = () => {
  const t = useRacketLabAdminT();
  const [filterOrg, setFilterOrg] = useState("");
  const [filterKind, setFilterKind] = useState("");
  const [filterResult, setFilterResult] = useState("");
  const [filterLimit, setFilterLimit] = useState(10);

  const params = {};
  if (filterOrg) params.organisation_id = filterOrg;
  if (filterKind) params.kind = filterKind;
  if (filterResult) params.result = filterResult;
  if (filterLimit) params.limit = filterLimit;

  const { data, loading, error } = useAdminApi(
    "/admin/qr-tokens/_ops/scan-audit-overview",
    { params },
  );

  return (
    <div data-testid="rl-admin-ops-scan-audit-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />

      <Link
        to="/admin/racket-lab/ops"
        data-testid="rl-ops-scan-audit-back"
        className="inline-flex w-fit items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("ops.common.back_to_toolkit")}
      </Link>

      <header>
        <h1 className="text-xl font-semibold text-slate-900">{t("ops.scan_audit.title")}</h1>
        <p className="text-xs text-slate-500">{t("ops.scan_audit.subtitle")}</p>
      </header>

      <section data-testid="rl-ops-scan-audit-filters" className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("ops.scan_audit.filters.org_label")}</span>
          <input
            data-testid="rl-ops-scan-audit-filter-org"
            value={filterOrg}
            onChange={(e) => setFilterOrg(e.target.value)}
            placeholder="TEAMSOFTWAREINNOVATIONS"
            className="rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("ops.scan_audit.filters.kind_label")}</span>
          <select
            data-testid="rl-ops-scan-audit-filter-kind"
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">{t("ops.scan_audit.filters.any_option")}</option>
            <option value="ticket">ticket</option>
            <option value="shop">shop</option>
            <option value="equipment">equipment</option>
            <option value="worker_personal">worker_personal</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("ops.scan_audit.filters.result_label")}</span>
          <select
            data-testid="rl-ops-scan-audit-filter-result"
            value={filterResult}
            onChange={(e) => setFilterResult(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">{t("ops.scan_audit.filters.any_option")}</option>
            <option value="ok">ok</option>
            <option value="denied_cross_org">denied_cross_org</option>
            <option value="denied_cross_shop">denied_cross_shop</option>
            <option value="rate_limited">rate_limited</option>
            <option value="expired">expired</option>
            <option value="revoked">revoked</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("ops.scan_audit.filters.limit_label")}</span>
          <input
            data-testid="rl-ops-scan-audit-filter-limit"
            type="number"
            min={1}
            max={100}
            value={filterLimit}
            onChange={(e) => setFilterLimit(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
            className="w-24 rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
          />
        </label>
      </section>

      {loading && <div data-testid="rl-ops-scan-audit-loading" className="rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-500">{t("ops.scan_audit.loading")}</div>}
      {error && !loading && (
        <div data-testid="rl-ops-scan-audit-error" className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          {t("ops.common.error_prefix")} {error.status || ""}: {error.detail || t("ops.common.error_unknown")}
        </div>
      )}

      {!loading && !error && data && (
        <ScanAuditOverviewCard data={data} dataTestId="rl-ops-scan-audit-card" />
      )}
    </div>
  );
};

export default RLAdminOpsScanAuditOverview;
